import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Home, Save, ImageIcon, X } from 'lucide-react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminHeroSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    title: '',
    subtitle: '',
    image_url: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/hero`);
      setSettings(response.data);
      setImagePreview(response.data.image_url);
    } catch (error) {
      console.error('Error carregant configuració:', error);
      setMessage('Error carregant configuració');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(settings.image_url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const formData = new FormData();
      formData.append('title', settings.title);
      formData.append('subtitle', settings.subtitle);
      
      if (imageFile) {
        formData.append('image', imageFile);
      }

      await axios.put(`${API}/settings/hero`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setMessage('Configuració guardada correctament! ✅');
      setImageFile(null);
      
      // Recarregar configuració
      await fetchSettings();
      
      // Netejar missatge després de 3 segons
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error guardant configuració:', error);
      setMessage('Error guardant configuració ❌');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregant...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="flex items-center gap-3 mb-8">
            <Home className="h-8 w-8 text-primary" />
            <h1 className="text-4xl font-heading font-medium text-foreground">
              Configuració Pàgina Principal
            </h1>
          </div>

          {message && (
            <div className={`mb-6 p-4 rounded-xl ${message.includes('Error') ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Títol */}
            <div className="bg-card rounded-2xl p-6 shadow-sm border border-border/50">
              <Label htmlFor="title" className="text-lg mb-3 block font-medium">
                Títol Principal
              </Label>
              <Input
                id="title"
                value={settings.title}
                onChange={(e) => setSettings({ ...settings, title: e.target.value })}
                placeholder="Una botiga amb ànima"
                className="text-lg"
                required
              />
              <p className="text-sm text-muted-foreground mt-2">
                Aquest és el títol gran que es veu a la pàgina principal
              </p>
            </div>

            {/* Subtítol */}
            <div className="bg-card rounded-2xl p-6 shadow-sm border border-border/50">
              <Label htmlFor="subtitle" className="text-lg mb-3 block font-medium">
                Subtítol / Descripció
              </Label>
              <Textarea
                id="subtitle"
                value={settings.subtitle}
                onChange={(e) => setSettings({ ...settings, subtitle: e.target.value })}
                placeholder="Després de gairebé 50 anys..."
                rows={4}
                className="text-base"
                required
              />
              <p className="text-sm text-muted-foreground mt-2">
                Text descriptiu que apareix sota el títol
              </p>
            </div>

            {/* Imatge */}
            <div className="bg-card rounded-2xl p-6 shadow-sm border border-border/50">
              <Label className="text-lg mb-3 block font-medium">
                Imatge Principal
              </Label>
              
              {imagePreview && (
                <div className="relative max-w-md rounded-2xl overflow-hidden bg-muted mb-4">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="w-full h-auto" 
                  />
                  {imageFile && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={clearImage}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}

              <Input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="cursor-pointer"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Imatge que apareix al costat del text (recomanat: 800x800px)
              </p>
            </div>

            {/* Botons */}
            <div className="flex gap-4">
              <Button
                type="submit"
                disabled={saving}
                className="flex-1"
                size="lg"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Guardant...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Guardar Canvis
                  </>
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminHeroSettings;
